/**
 * Mobile-only Mapbox gesture helpers.
 * Desktop (fine pointer / no touch) must keep pitch + rotate behavior intact.
 */

/**
 * True when the primary input is touch / coarse (phones, tablets, touch laptops).
 * Desktop mice report fine pointer and should return false.
 */
export function prefersTouchMapGestures(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win) return false;
  try {
    if (typeof win.matchMedia === 'function') {
      if (win.matchMedia('(pointer: coarse)').matches) return true;
      // Some devices expose coarse only as any-pointer
      if (win.matchMedia('(any-pointer: coarse)').matches && !win.matchMedia('(pointer: fine)').matches) {
        return true;
      }
    }
  } catch (_) {}
  try {
    if (typeof win.navigator?.maxTouchPoints === 'number' && win.navigator.maxTouchPoints > 0) {
      // Touch-capable fine-pointer desktops (e.g. Surface with mouse): prefer fine pointer path
      if (typeof win.matchMedia === 'function' && win.matchMedia('(pointer: fine)').matches) {
        return false;
      }
      return true;
    }
  } catch (_) {}
  return false;
}

/**
 * Prefer pinch-zoom + one-finger pan over pitch/rotate on touch devices.
 * No-ops when map handlers are missing (tests / partial mocks).
 */
export function applyMobileMapGestureHardening(map) {
  if (!map) return false;
  try {
    if (map.dragRotate && typeof map.dragRotate.disable === 'function') {
      map.dragRotate.disable();
    }
  } catch (_) {}
  try {
    if (map.touchZoomRotate && typeof map.touchZoomRotate.disableRotation === 'function') {
      map.touchZoomRotate.disableRotation();
    }
  } catch (_) {}
  try {
    if (map.touchPitch && typeof map.touchPitch.disable === 'function') {
      map.touchPitch.disable();
    }
  } catch (_) {}
  return true;
}

/**
 * Allow two-finger pitch + rotate while 3D mode is active on touch devices.
 * Call touchZoomRotate.enable() before enableRotation() (Mapbox-recommended order).
 */
export function enableMobileMap3DGestures(map) {
  if (!map) return false;
  try {
    if (map.touchZoomRotate) {
      if (typeof map.touchZoomRotate.enable === 'function') {
        map.touchZoomRotate.enable();
      }
      if (typeof map.touchZoomRotate.enableRotation === 'function') {
        map.touchZoomRotate.enableRotation();
      }
    }
  } catch (_) {}
  try {
    if (map.touchPitch && typeof map.touchPitch.enable === 'function') {
      map.touchPitch.enable();
    }
  } catch (_) {}
  try {
    if (map.dragRotate && typeof map.dragRotate.enable === 'function') {
      map.dragRotate.enable();
    }
  } catch (_) {}
  return true;
}

/**
 * Touch only: 3D → allow tilt/rotate; 2D → re-harden for pan/pinch.
 * Desktop / fine pointer: no-op (handlers stay as Mapbox defaults).
 */
export function syncMobileMapGesturesFor3D(map, is3D, options = {}) {
  const touch =
    typeof options.touchMapGestures === 'boolean'
      ? options.touchMapGestures
      : prefersTouchMapGestures();
  if (!map || !touch) return false;
  return is3D ? enableMobileMap3DGestures(map) : applyMobileMapGestureHardening(map);
}

/**
 * Mapbox Draw often disables dragPan during select/drag and can leave it off on touch.
 * Re-enable only when app overlays have not intentionally locked the map.
 */
export function reenableMapDragPanIfSafe(map) {
  if (!map || !map.dragPan) return false;
  try {
    if (map.__yona_isModalOpen || map.__yona_isPickerOpen) return false;
    if (typeof map.dragPan.isEnabled === 'function' && map.dragPan.isEnabled()) return false;
    if (typeof map.dragPan.enable === 'function') {
      map.dragPan.enable();
      return true;
    }
  } catch (_) {}
  return false;
}

const DRAW_PAN_RECOVERY_EVENTS = [
  'draw.selectionchange',
  'draw.modechange',
  'draw.create',
  'draw.update',
  'draw.delete'
];

/**
 * Bind Draw event + touchend recovery for dragPan. Returns an unbind function.
 */
export function bindDrawDragPanRecovery(map, options = {}) {
  if (!map || typeof map.on !== 'function') return () => {};
  const { includeTouchEnd = true } = options;
  const onRecover = () => {
    reenableMapDragPanIfSafe(map);
  };

  DRAW_PAN_RECOVERY_EVENTS.forEach((evt) => {
    try { map.on(evt, onRecover); } catch (_) {}
  });

  let canvas = null;
  if (includeTouchEnd && typeof map.getCanvas === 'function') {
    try {
      canvas = map.getCanvas();
      if (canvas && typeof canvas.addEventListener === 'function') {
        canvas.addEventListener('touchend', onRecover, { passive: true });
      }
    } catch (_) {
      canvas = null;
    }
  }

  return () => {
    DRAW_PAN_RECOVERY_EVENTS.forEach((evt) => {
      try { map.off && map.off(evt, onRecover); } catch (_) {}
    });
    if (canvas && typeof canvas.removeEventListener === 'function') {
      try { canvas.removeEventListener('touchend', onRecover); } catch (_) {}
    }
  };
}
